import { useForm } from "react-hook-form";
import { useTranslationApp } from "../../lib/i18next";
import ModalBasicLayout from "../Modal/ModalBasicLayout";
import ContainerModalActions from "../Containers/ContainerModalActions";
import TextField from "../Form/TextField";
import Box from "@mui/material/Box";
import { useEffect, useState } from "react";
import {
  BUTTON_CANCEL,
  BUTTON_CREATE,
  CALL_TO_ACTION_PLACEHOLDER,
  CATALOG_PLACEHOLDER,
  COVER_CONTENT_PLACEHOLDER,
  DESTINATION_URL_PLACEHOLDER,
  FIELD_REQUIRED,
  FIELD_URL_VALID,
  FIELDS_REQUIRED,
  MODAL_INSTANT_EXPERIENCE_CREATE_DESCRIPTION,
  MODAL_INSTANT_EXPERIENCE_CREATE_TITLE,
  PROMOTED_PRODUCTS_PLACEHOLDER,
} from "../../i18n/keysTranslations";
import { REGEX_URL } from "../../utils/regex";
import {
  ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_APP_PROMOTION_OBJECTIVE,
  IMAGE_FORMATS,
  META,
  VIDEO_FORMATS,
} from "../../utils/constants";
import { generalFormatMediaUrl } from "../../services/adsCampaigns";
import ContentField from "../Form/ContentField";
import {
  startCreateInstantExperience,
  startGetAdvertisableAppsAdsCampaigns,
} from "../../actions/adsCampaigns";
import { useDispatchApp } from "../../lib/redux";
import SelectProductsSetMeta from "../Platforms/Meta/Campaign/SelectProductsSetMeta";
import SelectCatalogMeta from "../Platforms/Meta/Campaign/SelectCatalogMeta";
import { getApplicationUrlMeta } from "../../services/meta";

const ModalInstantExperienceCreate = ({
  modalOpen,
  onCloseModal,
  integrationID,
  adAccountID,
  pageID,
  catalogID,
  productSetID,
  appID,
  destinationType,
  destinationUrl,
  mediaUrl,
  callToAction,
  onCallbackSave = () => {},
  disabledProductConfiguration,
  language,
}) => {
  const { t } = useTranslationApp();
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      catalogID: catalogID || "",
      productSetID: productSetID || "",
      callToAction: callToAction || "",
      destinationUrl: destinationUrl || "",
      mediaUrl: mediaUrl || "",
    },
  });
  const productSetValue = watch(`productSetID`);
  const catalogValue = watch(`catalogID`);
  const [submitting, setSubmitting] = useState(false);

  const linkIsDisabledForApp =
    appID &&
    ADS_CAMPAIGN_META_DESTINATION_TYPES_WITH_APP_PROMOTION_OBJECTIVE.includes(
      destinationType
    );

  const dispatch = useDispatchApp();

  const onSubmit = async (data) => {
    setSubmitting(true);
    const response = await dispatch(
      startCreateInstantExperience({
        ...data,
        integrationID,
        adAccountID,
        pageID,
        productSetID: productSetValue,
      })
    );

    if (response.ok) {
      onCallbackSave({
        instantExperience: response.data,
        productSetID: data.productSetID,
        catalogID: data.catalogID,
      });
    }

    setSubmitting(false);
  };

  const getAppInformation = async () => {
    const response = await dispatch(
      startGetAdvertisableAppsAdsCampaigns({
        integrationID,
        adAccountID,
        appID,
        platform: META,
      })
    );

    if (response.ok) {
      const app = response?.data?.[0];
      const appUrl = getApplicationUrlMeta({
        destinationType,
        appData: app,
      });
      setValue(`destinationUrl`, appUrl);
    }
  };

  useEffect(() => {
    if (!linkIsDisabledForApp) return;

    getAppInformation();
    // eslint-disable-next-line
  }, [linkIsDisabledForApp]);

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      fullWidth={true}
      maxWidth="xs"
      scroll="paper"
      title={t(MODAL_INSTANT_EXPERIENCE_CREATE_TITLE)}
      description={t(MODAL_INSTANT_EXPERIENCE_CREATE_DESCRIPTION)}
    >
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        {((productSetValue && catalogValue) || !productSetValue) && (
          <SelectCatalogMeta
            label={t(CATALOG_PLACEHOLDER)}
            disabled={disabledProductConfiguration}
            fullWidth
            variant="filled"
            platform={META}
            integrationID={integrationID}
            adAccountID={adAccountID}
            control={control}
            name={`catalogID`}
            onChangeAction={() => setValue(`productSetID`, "")}
            errors={errors}
          />
        )}
        <SelectProductsSetMeta
          fullWidth
          label={t(PROMOTED_PRODUCTS_PLACEHOLDER)}
          variant="filled"
          platform={META}
          disabled={disabledProductConfiguration}
          integrationID={integrationID}
          adAccountID={adAccountID}
          catalogID={catalogValue}
          control={control}
          name={`productSetID`}
          errors={errors}
          onChangeAction={(_, catalog) => {
            if (!catalogID && catalog) {
              setValue(`catalogID`, catalog.id);
            }
          }}
          rules={{
            required: {
              value: true,
              message: t(FIELDS_REQUIRED),
            },
          }}
        />
        <TextField
          fullWidth
          name="callToAction"
          variant="filled"
          label={t(CALL_TO_ACTION_PLACEHOLDER)}
          control={control}
          rules={{
            required: { value: true, message: t(FIELDS_REQUIRED) },
          }}
          errors={errors}
        />
        <TextField
          fullWidth
          variant="filled"
          name="destinationUrl"
          label={t(DESTINATION_URL_PLACEHOLDER)}
          disabled={linkIsDisabledForApp}
          control={control}
          rules={{
            required: { value: true, message: t(FIELDS_REQUIRED) },
            validate: (value) => {
              if (!REGEX_URL.test(value)) {
                return t(FIELD_URL_VALID);
              }
            },
          }}
          errors={errors}
        />
        <ContentField
          propsSelectContentComponent={{
            acceptedFormats: VIDEO_FORMATS.concat(IMAGE_FORMATS),
            showButtonUpload: true,
            showGenerateWithAIOption: false,
            showUploadDeviceOption: true,
            showGalleryOption: true,
            language,
          }}
          platform={META}
          sxPreview={{
            height: 120,
            width: 70,
          }}
          onCallbackSave={({ from, content }) => {
            const contentFormatted = generalFormatMediaUrl({
              content,
              from,
            });
            setValue(`mediaUrl`, contentFormatted.url);
          }}
          label={t(COVER_CONTENT_PLACEHOLDER)}
          name={`mediaUrl`}
          control={control}
          errors={errors}
          rules={{
            required: {
              value: true,
              message: t(FIELD_REQUIRED),
            },
          }}
        />
        <ContainerModalActions
          hasPadding={false}
          propsPrimaryButton={{
            showButton: true,
            text: BUTTON_CREATE,
            loading: submitting,
            type: "submit",
            onClick: () => {},
          }}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CANCEL,
            disabled: submitting,
            onClick: onCloseModal,
          }}
        />
      </Box>
    </ModalBasicLayout>
  );
};

export default ModalInstantExperienceCreate;
